import isEmpty from "lodash/isEmpty";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { useCustomValueParentChildValues } from "PFCore/hooks/queries/custom_fields/use_custom_value_parent_child_values";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useCustomValuePreviewContext } from "../../context/custom_value_preview_context";
import {
  CombinedParentChildData,
  combineExistingAndMissingParentChildSkills
} from "./parent_child_section.utils";
import { SubsectionContent } from "./subsection_content";

export const ParentChildSection = () => {
  const { t } = useTranslation("translation", { keyPrefix: "customValueSidepanel" });

  const { show, isClosing, customValueId, parentChildProfileValues } = useCustomValuePreviewContext();
  const enabled = Boolean(show && customValueId && !isClosing);

  const { data: parentChildData, isFetching: isFetchingParentChildData } =
    useCustomValueParentChildValues<CombinedParentChildData>(customValueId!, {
      enabled,
      select: (data) => combineExistingAndMissingParentChildSkills(data, parentChildProfileValues)
    });

  const getDescriptionText = useCallback(
    (hasParentValues?: boolean, hasChildValues?: boolean) => {
      if (hasParentValues && hasChildValues) {
        return t("availableParentAndChildSkills");
      }
      if (hasParentValues) {
        return t("availableParentSkills");
      }
      if (hasChildValues) {
        return t("availableChildSkills");
      }
      return null;
    },
    [t]
  );

  if (isFetchingParentChildData) {
    return <LoadingDots circlesEnabled circleSize="xs" centered />;
  }

  const { parents: parentSkills, children: childSkills } = parentChildData || { parents: [], children: [] };
  const hasChildSkills = !isEmpty(childSkills);
  const hasParentSkills = !isEmpty(parentSkills);

  return (
    <>
      <Divider />
      <Stack>
        <Typography variant="bodyRegular">
          {getDescriptionText(!isEmpty(parentSkills), !isEmpty(childSkills))}
        </Typography>
        <Tile paddingVariant="none" border>
          {hasChildSkills && (
            <SubsectionContent
              iconName="substitute-child"
              mainTitle={t("childSkills")}
              sideText={`(${t("moreGranular")})`}
              skills={childSkills}
            />
          )}
          {hasChildSkills && hasParentSkills && <Divider />}
          {hasParentSkills && (
            <SubsectionContent
              iconName="substitute-parent"
              mainTitle={t("parentSkills")}
              sideText={`(${t("lessGranular")})`}
              skills={parentSkills}
            />
          )}
        </Tile>
      </Stack>
    </>
  );
};
